import { useAuth } from '@hooks/useAuth';
import { AppState } from '@store/index';
import { setDashboardFilter } from '@store/reducers/adminDashboardReducer';
import React, {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { io, Socket } from 'socket.io-client';
import {
    clearPendingWaitStorage,
    COOLDOWN_MS,
    DashboardSnapshotSynced,
    PendingWait,
    readPendingWait,
    SyncSettledPayload,
    writePendingWait,
} from './dashboardSnapshotSyncSession';

const SOCKET_SERVER_URL = process.env.SOCKET_SERVER_URL;
const LOG_PREFIX = '[DashboardSnapshotSync]';

type DashboardSnapshotSyncContextValue = {
    socketReady: boolean;
    pendingWait: PendingWait | null;
    registerPendingSync: (taskId: string) => void;
    clearPendingSync: () => void;
    addSyncSettledListener: (listener: (payload: SyncSettledPayload) => void) => () => void;
};

const DashboardSnapshotSyncContext =
    createContext<DashboardSnapshotSyncContextValue | null>(null);

export function useDashboardSnapshotSync() {
    const ctx = useContext(DashboardSnapshotSyncContext);
    if (!ctx) {
        throw new Error(
            'useDashboardSnapshotSync must be used within DashboardSnapshotSyncProvider',
        );
    }
    return ctx;
}

export const DashboardSnapshotSyncProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { auth } = useAuth();
    const dashboardFilter = useSelector((state: AppState) => state.adminDashboard.dashboardFilter);
    const [socketReady, setSocketReady] = useState(false);
    const [pendingWait, setPendingWait] = useState<PendingWait | null>(() =>
        readPendingWait(),
    );

    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const listenersRef = useRef(new Set<(payload: SyncSettledPayload) => void>());
    const filterRef = useRef(dashboardFilter);
    filterRef.current = dashboardFilter;
    const tRef = useRef(t);
    tRef.current = t;

    const emitSettled = useCallback((payload: SyncSettledPayload) => {
        listenersRef.current.forEach((listener) => listener(payload));
    }, []);

    const clearPendingTimeout = useCallback(() => {
        if (pendingTimeoutRef.current) {
            clearTimeout(pendingTimeoutRef.current);
            pendingTimeoutRef.current = null;
        }
    }, []);

    const clearPendingSync = useCallback(() => {
        clearPendingTimeout();
        clearPendingWaitStorage();
        setPendingWait(null);
    }, [clearPendingTimeout]);

    const schedulePendingTimeout = useCallback(
        (waitUntil: number) => {
            clearPendingTimeout();
            const remainingMs = Math.max(0, waitUntil - Date.now());
            if (remainingMs <= 0) {
                clearPendingSync();
                return;
            }
            pendingTimeoutRef.current = setTimeout(() => {
                pendingTimeoutRef.current = null;
                clearPendingWaitStorage();
                setPendingWait(null);
                console.warn(`${LOG_PREFIX} socket wait timed out after`, remainingMs, 'ms');
            }, remainingMs);
        },
        [clearPendingSync, clearPendingTimeout],
    );

    const registerPendingSync = useCallback(
        (taskId: string) => {
            const waitUntil = Date.now() + COOLDOWN_MS;
            writePendingWait(taskId, waitUntil);
            const pending: PendingWait = { task_id: taskId, wait_until: waitUntil };
            setPendingWait(pending);
            schedulePendingTimeout(waitUntil);
        },
        [schedulePendingTimeout],
    );

    const addSyncSettledListener = useCallback(
        (listener: (payload: SyncSettledPayload) => void) => {
            listenersRef.current.add(listener);
            return () => {
                listenersRef.current.delete(listener);
            };
        },
        [],
    );

    const handleSyncedRef = useRef<(msg: DashboardSnapshotSynced) => void>(() => undefined);

    handleSyncedRef.current = (msg) => {
        const pending = readPendingWait();
        if (!pending) return;
        if (msg.task_id && pending.task_id !== msg.task_id) {
            console.warn(`${LOG_PREFIX} ignored event (task_id mismatch)`, {
                expected: pending.task_id,
                received: msg.task_id,
            });
            return;
        }

        clearPendingSync();

        const status = msg.status === 'success' ? 'success' : 'failed';
        emitSettled({ status, error: msg.error, task_id: msg.task_id });

        if (status === 'success') {
            // toast.success(tRef.current('dashboard.syncCompleted'));
            dispatch(setDashboardFilter({ ...filterRef.current }));
            return;
        }
        toast.error(msg.error || tRef.current('dashboard.syncFailed'));
    };

    useEffect(() => {
        if (!pendingWait) return;
        schedulePendingTimeout(pendingWait.wait_until);
    }, [pendingWait?.task_id, pendingWait?.wait_until, schedulePendingTimeout]);

    useEffect(() => () => clearPendingTimeout(), [clearPendingTimeout]);

    useEffect(() => {
        const userId = auth?.user_id;
        if (!userId || !SOCKET_SERVER_URL) {
            setSocketReady(false);
            return;
        }

        setSocketReady(false);
        const socket: Socket = io(SOCKET_SERVER_URL, {
            transports: ['websocket'],
            reconnection: true,
            query: { user_id: userId },
        });

        const handleSynced = (msg: DashboardSnapshotSynced) => {
            handleSyncedRef.current(msg);
        };

        const logSocketResponse = (eventName: string, ...args: unknown[]) => {
            const payload = args.length <= 1 ? args[0] : args;
            console.log(`${LOG_PREFIX} socket response`, {
                event: eventName,
                payload,
                payloadJson: JSON.stringify(payload),
            });
        };

        socket.onAny(logSocketResponse);

        socket.on('connect', () => {
            setSocketReady(false);
            console.log(`${LOG_PREFIX} socket connected`, {
                socketId: socket.id,
                userId,
                url: SOCKET_SERVER_URL,
            });
            socket.emit('join_room', { user_id: userId });
        });
        socket.on('joined_room', (data: { status?: string }) => {
            setSocketReady(data?.status === 'success');
        });
        socket.on('connect_error', (err) => {
            setSocketReady(false);
            console.warn(`${LOG_PREFIX} socket connect_error`, err.message);
        });
        socket.on('disconnect', (reason) => {
            setSocketReady(false);
            console.log(`${LOG_PREFIX} socket disconnect`, { reason });
        });
        socket.on('dashboard_snapshot_synced', handleSynced);

        return () => {
            setSocketReady(false);
            socket.offAny(logSocketResponse);
            socket.off('connect');
            socket.off('joined_room');
            socket.off('connect_error');
            socket.off('disconnect');
            socket.off('dashboard_snapshot_synced', handleSynced);
            socket.close();
        };
    }, [auth?.user_id]);

    const value = useMemo(
        () => ({
            socketReady,
            pendingWait,
            registerPendingSync,
            clearPendingSync,
            addSyncSettledListener,
        }),
        [
            socketReady,
            pendingWait,
            registerPendingSync,
            clearPendingSync,
            addSyncSettledListener,
        ],
    );

    return (
        <DashboardSnapshotSyncContext.Provider value={value}>
            {children}
        </DashboardSnapshotSyncContext.Provider>
    );
};
